import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Code2, Users, Globe2, Award, Target, Lightbulb, Rocket } from 'lucide-react';
import { fadeInOnScroll, scaleOnScroll } from '../utils/animations';

gsap.registerPlugin(ScrollTrigger);

const About = () => {
  const headerRef = useRef(null);
  const statsRef = useRef(null);
  const valuesRef = useRef(null);

  useEffect(() => {
    // Header animation
    if (headerRef.current) {
      gsap.fromTo(
        headerRef.current.children,
        { opacity: 0, y: 100 },
        { opacity: 1, y: 0, stagger: 0.2, duration: 1, ease: 'power3.out' }
      );
    }

    // Stats animation
    if (statsRef.current) {
      const stats = statsRef.current.querySelectorAll('.stat-item');
      stats.forEach((stat) => {
        const value = stat.querySelector('.stat-value');
        const target = parseInt(value.dataset.target);

        gsap.to(value, {
          textContent: target,
          duration: 2,
          ease: 'power2.out',
          snap: { textContent: 1 },
          scrollTrigger: {
            trigger: stat,
            start: 'top 80%',
            once: true,
          },
          onUpdate: function() {
            value.textContent = Math.ceil(this.targets()[0].textContent) + (value.dataset.suffix || '');
          }
        });
      });
    }

    // Values cards
    if (valuesRef.current) {
      const cards = valuesRef.current.querySelectorAll('.value-card');
      fadeInOnScroll(cards, { stagger: 0.2 });
    }

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  return (
    <div className="about-page pt-32">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 mb-32">
        <div ref={headerRef}>
          <span className="inline-block px-3 py-1 bg-[var(--surface-container-high)] text-[var(--on-surface-variant)] font-body text-xs tracking-code uppercase mb-6 rounded-sm">
            About Us
          </span>
          <h1 className="font-headline text-5xl md:text-7xl lg:text-8xl font-bold tracking-headline uppercase mb-12 leading-[0.95]">
            Building the <br />
            <span className="text-[var(--primary)] text-neon-glow">Future of Code</span>
          </h1>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <p className="font-body text-[var(--on-surface-variant)] text-xl leading-relaxed mb-6">
                We are a collective of engineers, architects, and technical innovators who believe that software should be elegant, performant, and built to last.
              </p>
              <p className="font-body text-[var(--on-surface-variant)] text-lg leading-relaxed">
                Our approach combines rigorous technical standards with creative problem-solving, delivering systems that scale from startup MVPs to enterprise platforms processing millions of transactions.
              </p>
            </div>
            <div className="glass-panel p-10 border border-[var(--outline-variant)]/20 relative">
              <h3 className="font-headline text-2xl font-bold uppercase mb-6">Our Mission</h3>
              <p className="font-body text-[var(--on-surface-variant)] leading-relaxed mb-6">
                To architect digital ecosystems that don't just meet today's needs, but anticipate tomorrow's challenges. We transform ambitious visions into production-ready systems that deliver measurable impact.
              </p>
              <div className="absolute -bottom-4 -right-4 w-64 h-64 bg-[var(--primary)]/10 blur-[100px] rounded-full" />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section ref={statsRef} className="py-24 bg-[var(--surface-container-lowest)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { icon: Code2, value: 150, suffix: '+', label: 'Projects Delivered' },
              { icon: Award, value: 12, suffix: '+', label: 'Years Experience' },
              { icon: Users, value: 45, suffix: '+', label: 'Team Members' },
              { icon: Globe2, value: 28, suffix: '+', label: 'Countries Served' },
            ].map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div
                  key={index}
                  className="stat-item group text-center p-8 bg-[var(--surface-container-low)] hover:bg-[var(--surface-container-high)] transition-all duration-300 relative overflow-hidden"
                >
                  <div className="absolute top-0 left-0 w-full h-1 bg-[var(--primary)] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />

                  <div className="flex justify-center mb-6">
                    <Icon
                      size={40}
                      className="text-[var(--primary)] opacity-60 group-hover:opacity-100 transition-opacity"
                      strokeWidth={1.5}
                    />
                  </div>

                  <div
                    className="stat-value font-headline text-4xl lg:text-5xl font-bold text-[var(--on-surface)] mb-3"
                    data-target={stat.value}
                    data-suffix={stat.suffix}
                  >
                    0{stat.suffix}
                  </div>

                  <div className="font-body text-xs tracking-code uppercase text-[var(--on-surface-variant)]">
                    {stat.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="font-headline text-4xl md:text-6xl font-bold tracking-headline uppercase mb-6">
              Core<span className="text-[var(--primary)]"> Values</span>
            </h2>
            <p className="font-body text-[var(--on-surface-variant)] text-lg max-w-2xl mx-auto">
              The principles that guide everything we build
            </p>
          </div>

          <div ref={valuesRef} className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Target,
                title: 'Architecture First',
                description: 'Every line of code serves a strategic purpose. We design systems with intention, not improvisation.',
              },
              {
                icon: Lightbulb,
                title: 'Performance Obsessed',
                description: 'Milliseconds matter in production systems. We optimize relentlessly for speed and efficiency.',
              },
              {
                icon: Rocket,
                title: 'Future-Proof',
                description: 'Built for scale, designed for evolution. Our systems grow with your ambitions.',
              },
            ].map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  className="value-card group bg-[var(--surface-container-low)] p-10 hover:bg-[var(--surface-container-high)] transition-all duration-500 relative overflow-hidden"
                >
                  <div className="absolute top-0 left-0 w-full h-1 bg-[var(--primary)] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />

                  <div className="mb-8">
                    <Icon
                      size={56}
                      className="text-[var(--primary)] opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all duration-300"
                      strokeWidth={1.5}
                    />
                  </div>

                  <h3 className="font-headline text-2xl font-bold uppercase mb-4">
                    {value.title}
                  </h3>

                  <p className="font-body text-[var(--on-surface-variant)] leading-relaxed">
                    {value.description}
                  </p>

                  <div className="absolute bottom-0 right-0 w-32 h-32 bg-[var(--primary)]/5 blur-3xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team Philosophy */}
      <section className="py-32 bg-[var(--surface-container-lowest)]">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-headline text-4xl md:text-5xl font-bold tracking-headline uppercase mb-8">
                Why <span className="text-[var(--primary)]">Teams</span> Choose Us
              </h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-2 h-2 bg-[var(--primary)] rounded-full mt-2 flex-shrink-0" />
                  <div>
                    <h4 className="font-headline text-lg font-bold uppercase mb-2">Deep Technical Expertise</h4>
                    <p className="font-body text-[var(--on-surface-variant)]">
                      Our engineers have built systems processing billions of requests, handling petabytes of data, and serving millions of users.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-2 h-2 bg-[var(--primary)] rounded-full mt-2 flex-shrink-0" />
                  <div>
                    <h4 className="font-headline text-lg font-bold uppercase mb-2">End-to-End Ownership</h4>
                    <p className="font-body text-[var(--on-surface-variant)]">
                      From initial architecture to production deployment and beyond, we're with you every step of the way.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-2 h-2 bg-[var(--primary)] rounded-full mt-2 flex-shrink-0" />
                  <div>
                    <h4 className="font-headline text-lg font-bold uppercase mb-2">Agile & Transparent</h4>
                    <p className="font-body text-[var(--on-surface-variant)]">
                      Regular updates, clear communication, and iterative development ensure you're always in control.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square bg-gradient-to-br from-[var(--primary)]/20 to-[var(--secondary)]/20 rounded-lg relative overflow-hidden">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzhmZjVmZiIgc3Ryb2tlLXdpZHRoPSIxIiBvcGFjaXR5PSIwLjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <Code2 size={120} className="text-[var(--primary)] opacity-20 mx-auto mb-4" strokeWidth={1} />
                    <p className="font-headline text-2xl font-bold tracking-code uppercase text-[var(--primary)]">
                      Excellence in Code
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-headline text-4xl md:text-5xl font-bold tracking-headline uppercase mb-8">
            Ready to <span className="text-[var(--primary)]">Partner</span> with Us?
          </h2>
          <p className="font-body text-[var(--on-surface-variant)] text-lg mb-12">
            Let's discuss how we can help you achieve your technical goals
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="magnetic bg-gradient-to-br from-[var(--primary)] to-[var(--primary-container)] text-[var(--on-primary-container)] px-10 py-4 font-headline font-bold text-sm uppercase tracking-code hover:brightness-110 hover:scale-105 transition-all duration-300 rounded-none"
            >
              Get in Touch
            </Link>
            <Link
              to="/work"
              className="magnetic border-2 border-[var(--outline-variant)]/40 text-[var(--on-surface)] px-10 py-4 font-headline font-bold text-sm uppercase tracking-code hover:bg-[var(--surface-container)] hover:border-[var(--primary)] transition-all duration-300 rounded-none"
            >
              View Our Work
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
